// ✅ Professional UI without extra popups
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  VStack,
  SimpleGrid,
  Input,
  Select,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Card,
  CardBody,
  CardHeader,
  useToast,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaEdit,
  FaHistory,
  FaCheck,
  FaTimes,
  FaArrowLeft,
  FaSave
} from "react-icons/fa";
import VendorSelection from "../Credit/VendorSelection";
import { get_all_vendor_list } from "../../redux/vendorSlice";
import { fetchEquipmentData, updateEquipmentStatus } from "../../redux/EquipmentSlice";
import { ArrowLeftIcon } from "lucide-react";
import CustomButton from "../../components/CustomButton";
import { BiArrowBack } from "react-icons/bi";
import { FiEye } from "react-icons/fi";

export default function EquipmentDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isViewOnly = location.state?.isViewOnly ?? false;
  const toast = useToast();
  const [selectedVendor, setSelectedVendor] = useState([]);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [productPage, setProductPage] = useState(1);
  const [productRowsPerPage, setProductRowsPerPage] = useState(10);
  const [vendorError, setVendorError] = React.useState("");
  const [selectedProductReqQty, setSelectedProductReqQty] = useState(null);
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  const { id: paramId } = useParams();
  const equipmentId = location.state?.id || paramId; // ✅ fallback to param

  const { data, loading, error } = useSelector((state) => state.equipment);
  const { all_vendor_list } = useSelector((state) => state.vendors);

  const currentEquipment = data?.find(item => item.id === equipmentId);

  const [editingProduct, setEditingProduct] = useState(null);
  const [vendorLocked, setVendorLocked] = useState(false); // tracks if vendor already chosen
  const [editFormData, setEditFormData] = useState({});
  const [showLogs, setShowLogs] = useState(false);
  const [selectedProductLogs, setSelectedProductLogs] = useState([]);
  const [isTxnOpen, setIsTxnOpen] = useState(false);
  const [selectedTransactionLogs, setSelectedTransactionLogs] = useState([]);
  const [txnPage, setTxnPage] = useState(1);
  const [txnRowsPerPage, setTxnRowsPerPage] = useState(5);
  const { isOpen: isLogsOpen, onOpen: onLogsOpen, onClose: onLogsClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  // ✅ Updated theme colors
  const primaryColor = "#169e49";
  const primaryLight = "#e6f4ec";

  const totalProductPages = Math.ceil(
    (currentEquipment?.details?.length || 0) / productRowsPerPage
  );

  useEffect(() => {
  }, [selectedVendor]);

  // Get paginated products
  const paginatedProducts = currentEquipment?.details?.slice(
    (productPage - 1) * productRowsPerPage,
    productPage * productRowsPerPage
  );

  useEffect(() => {
    if (!equipmentId) {
      navigate(-1);
      return;
    }

    if (!data || data.length === 0) {
      dispatch(fetchEquipmentData());
    }

    dispatch(get_all_vendor_list());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentId]);
  

  const getStatusBadge = (status) => {
    switch (String(status)) {
      case "0": return { color: "yellow", text: "Under Review" };
      case "1": return { color: "red", text: "Rejected" };
      case "2": return { color: "green", text: "Approved" };
      default: return { color: "gray", text: "Unknown" };
    }
  };

  // ✅ In handleEditProduct function
  const handleEditProduct = (product) => {
    setEditingProduct(product);

    setEditFormData({
      id: product.id,
      quantity: product.status === "2"
        ? product.quantity
        : product.req_quantity,
      creditLimitControl: "2", // Always set to BLOCK by default
    });

    // ✅ Parse vendor_id properly
    if (product.vendor_id) {
      const vendorIds = product.vendor_id
        .toString()
        .split(",")
        .map(id => parseInt(id.trim()))
        .filter(id => !isNaN(id) && id > 0);
      setSelectedVendor(vendorIds);
    } else if (!vendorLocked) {
      setSelectedVendor([]);
    }

    onEditOpen();
  };

  const openTransactionLogs = (transactions) => {
    if (transactions && transactions.length > 0) {
      const allReponselist = transactions.flatMap(txn => txn.reponselist || []);
      setSelectedTransactionLogs(allReponselist);
    } else {
      setSelectedTransactionLogs([]);
    }
    setTxnPage(1);
    setIsTxnOpen(true);
  };

  useEffect(() => {
    if (Array.isArray(currentEquipment?.vendors)) {
      const validVendors = currentEquipment.vendors
        .filter((v) => {
          // ✅ More strict validation
          const isValid = v !== null &&
            v !== undefined &&
            v !== "" &&
            v !== 0 &&
            !isNaN(v);
          return isValid;
        })
        .map(v => typeof v === 'string' ? parseInt(v) : v); // Convert to numbers

      if (validVendors.length > 0) {
        setSelectedVendor(validVendors);
        setVendorLocked(true);
      } else {
        setSelectedVendor([]); // ensure it's empty
        setVendorLocked(false);
      }
    } else {
      // ✅ If vendors is not an array, reset
      setSelectedVendor([]);
      setVendorLocked(false);
    }
  }, [currentEquipment?.id]);

  const executeAction = async (actionType, productIds, extraData = null) => {
    try {
      if (!currentEquipment) return;

      let payload = {
        equipmentId: currentEquipment?.id,
        userid: currentEquipment?.customerid,
        productIds,
      };

      if (
        (actionType === "approve" || actionType === "update") &&
        (!vendorLocked && (!selectedVendor || selectedVendor.length === 0))
      ) {
        setVendorError("Vendor selection is mandatory for first approval.");
        return;
      }

      if (actionType === "approve" || actionType === "reject") {
        payload.status = actionType === "approve" ? "2" : "1";

        payload.updates = productIds.reduce((acc, id) => {
          let vendorIds = [];

          if (Array.isArray(selectedVendor) && selectedVendor.length > 0) {
            vendorIds = selectedVendor.filter(vendorId => vendorId);
          }

          // ✅ FIX: Send vendor IDs as separate array elements, not joined string
          acc[id] = {
            vendor_id: vendorIds,  // Send as array, not joined string
            cd_type_limit: "2" // Always send BLOCK
          };

          return acc;
        }, {});

        if (actionType === "approve" && !vendorLocked) {
          setVendorLocked(true);
        }
      }

      if (actionType === "update") {
        const currentProduct = currentEquipment.details.find(p => p.id === productIds[0]);
        payload.status = extraData?.forceApprove ? "2" : currentProduct.status;

        payload.updates = productIds.reduce((acc, id) => {
          let vendorIds = [];

          if (Array.isArray(selectedVendor) && selectedVendor.length > 0) {
            vendorIds = selectedVendor.filter(vendorId => vendorId);
          }

          // ✅ FIX: Send vendor IDs as separate array elements, not joined string
          acc[id] = {
            vendor_id: vendorIds,  // Send as array, not joined string
            quantity: extraData?.quantity,
            cd_type_limit: "2", // Always send BLOCK
          };
          return acc;
        }, {});
      }

      await dispatch(updateEquipmentStatus(payload)).unwrap();
      dispatch(fetchEquipmentData());

      toast({
        title: actionType === "reject" ? "Rejected" : "Success",
        description:
          actionType === "reject"
            ? "Products have been rejected."
            : `Products have been ${actionType === "update" ? "updated" : actionType + "d"}.`,
        status: actionType === "reject" ? "error" : "success",
        duration: 3000,
        isClosable: true,
        variant: "solid",
      })
      onEditClose();
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to execute action",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="50vh">
        <Spinner size="xl" color={primaryColor} />
      </Flex>
    );
  }

  if (error || !currentEquipment) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error || "Equipment not found"}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      {/* Header */}
      <Card bg="#fff"
        rounded="md"
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
        fontWeight="bold"
        fontSize={{ base: "md", md: "lg" }}>
        <Text>Equipment Details</Text>

        <HStack spacing={2}>
          <Select
            size="sm"
            w="80px"
            value={productRowsPerPage}
            onChange={(e) => {
              setProductRowsPerPage(Number(e.target.value));
              setProductPage(1);
            }}
          >
            {[5, 10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
          <CustomButton
            icon={<BiArrowBack color="#fff" />}
            title={"Back"}
            height={"30px"}
            b_color={"#000"}
            onClick={() => navigate(-1)}
          />
        </HStack>

      </Card>

      {/* Products Table */}
      <Card mb={6}>
        <CardBody p={0}>
          <Box overflowX="auto">
            <Table size="md" minW="1200px">
              <Thead>
                <Tr bg={primaryLight}>
                  <Th>Product Type</Th>
                  <Th>Product Code</Th>
                  <Th>Product Name</Th>
                  <Th>Requested Quantity</Th>
                  <Th>Assigned Vendor</Th>
                  <Th>Approved Quantity</Th>
                  <Th>Credit Equipment</Th>
                  <Th>Status</Th>
                  <Th>Last Updated</Th>
                  <Th textAlign="center">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedProducts?.map((product) => {
                  const statusBadge = getStatusBadge(product.status);
                  const isApprovedOrRejected =
                    product.status === "2" || product.status === "1";

                  return (
                    <Tr key={product.id} _hover={{ bg: "gray.50" }}>
                      <Td>{product.prod_type}</Td>
                      <Td color={primaryColor} fontWeight="500">
                        {product.prod_code}
                      </Td>
                      <Td>{product.product_name}</Td>
                      <Td>{product.req_quantity}</Td>
                      <Td>
                        {product.vendor && product.vendor.length > 0 ? (
                          product.vendor
                            .map((v) => v.vendor_name) // extract vendor_name
                            .filter(Boolean) // remove null values
                            .join(", ") || "Not Assigned"
                        ) : (
                          "Not Assigned"
                        )}
                      </Td>

                      <Td>
                        {product.quantity ||
                          (product.logs?.length > 0
                            ? [...product.logs]
                              .sort(
                                (a, b) =>
                                  new Date(b.datetime) - new Date(a.datetime)
                              )[0].quantity
                            : "N/A")}
                      </Td>
                      <Td>
                        BLOCK
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={statusBadge.color}
                          borderRadius="md"
                          px={2}
                          py={1}
                        >
                          {statusBadge.text}
                        </Badge>
                      </Td>
                      <Td>{new Date(product.datetime).toLocaleDateString()}</Td>
                      <Td>
                        <Flex
                          justify="center"
                          align="center"
                          gap={3}
                          flexWrap="nowrap"
                        >
                          {isApprovedOrRejected ? (
                              <>
                            {!isViewOnly && (
                              <Tooltip label="Edit">
                              <IconButton
                                icon={<FaEdit />}
                                size="md"
                                colorScheme="green"
                                variant="solid"
                                onClick={() => handleEditProduct(product)}
                              />
                            </Tooltip>
                            )}
                              
                              <Tooltip label="Logs">
                                <IconButton
                                  icon={<FaHistory />}
                                  size="md"
                                  colorScheme="purple"
                                  variant="solid"
                                  onClick={() => {
                                    setSelectedProductLogs(product.logs || []);
                                    setSelectedProductReqQty(product.req_quantity); // ✅ store req_quantity
                                    onLogsOpen();
                                  }}
                                />
                              </Tooltip>
                              <Tooltip label="transaction logs">
                              <IconButton
                              icon={<FiEye />}
                              size="md"
                              colorScheme="teal"
                              onClick={() => openTransactionLogs(currentEquipment.transactions)}
                              >
                              </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                colorScheme="green"
                                onClick={() => {
                                  if (!vendorLocked) {
                                    // ✅ First approval → open edit modal with vendor selection
                                    handleEditProduct(product);
                                  } else {
                                    // ✅ Subsequent approvals → auto approve with saved vendor
                                    executeAction("approve", [product.id]);
                                  }
                                }}
                              >
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                colorScheme="red"
                                onClick={() =>
                                  executeAction("reject", [product.id])
                                }
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>

          {/* ✅ Pagination Controls */}
          <Flex
            justify="space-between"
            align="center"
            mt={4}
            px={4}
            flexWrap="wrap"
            gap={4}
          >
            {/* Rows Per Page */}
            <HStack></HStack>
            {/* Pagination Buttons */}
            <HStack spacing={3} mb={6}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProductPage((p) => Math.max(p - 1, 1))}
                isDisabled={productPage === 1}
              >
                Previous
              </Button>
              <Text fontSize="sm" color="gray.600">
                Page {productPage} of {totalProductPages}
              </Text>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setProductPage((p) => Math.min(p + 1, totalProductPages))
                }
                isDisabled={productPage >= totalProductPages}
              >
                Next
              </Button>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Product - {editingProduct?.product_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
              {/* Approved Quantity (editable always) */}
              <Box>
                <Text fontSize="sm" mb={1} fontWeight="medium">
                  Quantity <Text as="span" color="red.500">*</Text>
                </Text>
                <Input
                  value={editFormData.quantity}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, quantity: e.target.value })
                  }
                  size="sm"
                  placeholder="Enter Approved Quantity"
                  type="number"
                />
              </Box>

              {/* Control Type */}
              <Box>
                <Text fontSize="sm" mb={1} fontWeight="medium">
                  Control Type
                </Text>
                <Select
                  value={editFormData.creditLimitControl}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      creditLimitControl: e.target.value,
                    })
                  }
                  size="sm"
                  isDisabled={true}
                >
                  <option value="2">BLOCK</option>
                </Select>
              </Box>
            </SimpleGrid>

            <VendorSelection
              all_vendor_list={all_vendor_list}
              selectedVendor={selectedVendor}
              setSelectedVendor={setSelectedVendor}
              vendorError={vendorError}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>Cancel</Button>
            <Button
  bg={primaryColor}
  color="white"
  _hover={{ bg: "#12853c" }}
  leftIcon={<FaSave />}
  onClick={onConfirmOpen}
>
  Save Changes
</Button>


          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Logs Modal */}
      <Modal isOpen={isLogsOpen} onClose={onLogsClose} size="6xl">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" boxShadow="2xl" overflow="hidden">
          <ModalHeader
            bg={primaryColor}
            color="white"
            fontSize="xl"
            fontWeight="bold"
            py={4}
          >
            Product Modification History
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            {selectedProductLogs.length === 0 ? (
              <Text textAlign="center" py={8} color="gray.500" fontSize="md">
                No modification history available
              </Text>
            ) : (
              <>
                {/* Logs Table */}
                <Box
                  overflowX="auto"
                  borderWidth="1px"
                  borderRadius="lg"
                  boxShadow="sm"
                >
                  <Table size="sm" variant="striped" colorScheme="gray">
                    <Thead

                      position="sticky"
                      top={0}
                      zIndex={1}
                    >
                      <Tr>
                        <Th>Date & Time</Th>
                        <Th>Status</Th>
                        <Th>Approved Quantity</Th>
                        <Th>Requested Quantity</Th>
                        <Th>Control Type</Th>

                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedProductLogs
                        .slice()
                        .sort((a, b) => new Date(b.datetime) - new Date(a.datetime))
                        .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                        .map((log, i) => {
                          const statusBadge = getStatusBadge(log.status);
                          return (
                            <Tr key={i}>
                              <Td fontSize="sm" whiteSpace="nowrap">
                                {new Date(log.datetime).toLocaleString()}
                              </Td>
                              <Td>
                                <Badge
                                  colorScheme={statusBadge.color}
                                  px={2}
                                  py={1}
                                  borderRadius="md"
                                >
                                  {statusBadge.text}
                                </Badge>
                              </Td>
                              <Td>{log.quantity || "N/A"}</Td>   {/* ⬅️ Show quantity */}
                              <Td>{selectedProductReqQty || "N/A"}</Td>
                              <Td>
                                BLOCK
                              </Td>
                            </Tr>
                          );
                        })}
                    </Tbody>

                  </Table>
                </Box>

                {/* Pagination Controls */}
                <Flex
                  justify="space-between"
                  align="center"
                  mt={6}
                  flexWrap="wrap"
                  gap={4}
                >
                  {/* Rows Per Page */}
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">
                      Rows per page:
                    </Text>
                    <Select
                      size="sm"
                      w="80px"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      {[5, 10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </Select>
                  </HStack>

                  {/* Pagination Buttons */}
                  <HStack spacing={3}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      isDisabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {page} of{" "}
                      {Math.ceil(selectedProductLogs.length / rowsPerPage)}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setPage((p) =>
                          Math.min(
                            p + 1,
                            Math.ceil(selectedProductLogs.length / rowsPerPage)
                          )
                        )
                      }
                      isDisabled={
                        page >=
                        Math.ceil(selectedProductLogs.length / rowsPerPage)
                      }
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onLogsClose}
              colorScheme="purple"
              variant="solid"
              borderRadius="lg"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isTxnOpen} onClose={() => setIsTxnOpen(false)} size="6xl">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" boxShadow="2xl" overflow="hidden">
          <ModalHeader
            bg={primaryColor}
            color="white"
            fontSize="xl"
            fontWeight="bold"
            py={4}
          >
            Transaction Logs
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            {selectedTransactionLogs.length === 0 ? (
              <Text textAlign="center" py={8} color="gray.500" fontSize="md">
                No transaction history available
              </Text>
            ) : (
              <>
                <Box overflowX="auto" borderWidth="1px" borderRadius="lg" boxShadow="sm">
                  <Table size="sm" variant="striped" colorScheme="gray">
                    <Thead position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th>Invoice No</Th>
                        <Th>Date & Time</Th>
                        <Th>Customer Name</Th>
                        <Th>Business Name</Th>
                        <Th>Mobile</Th>
                        <Th>Payment Mode</Th>
                        <Th>Total Amount</Th>
                        <Th>Cash Received</Th>
                        <Th>Delivery OTP</Th>

                      </Tr>
                    </Thead>
                    <Tbody>
                      {selectedTransactionLogs
                        .slice((txnPage - 1) * txnRowsPerPage, txnPage * txnRowsPerPage)
                        .map((txn, idx) => (
                          <Tr key={idx}>
                            <Td>{txn.inv_no || "N/A"}</Td>
                            <Td>{txn.datetime ? new Date(txn.datetime).toLocaleString() : "N/A"}</Td>
                            <Td>{txn.customer_name || "N/A"}</Td>
                            <Td>{txn.customer_business_name || "N/A"}</Td>
                            <Td>{txn.mobile || "N/A"}</Td>
                            <Td>{txn.paymentmode || "N/A"}</Td>
                            <Td>{txn.totalamount || "0.00"}</Td>
                            <Td>{txn.cash_received || "0.00"}</Td>
                            <Td>{txn.delivery_otp || "N/A"}</Td>
                          </Tr>
                        ))}
                    </Tbody>
                  </Table>
                </Box>

                {/* Pagination Controls */}
                <Flex justify="space-between" align="center" mt={6} flexWrap="wrap" gap={4}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">Rows per page:</Text>
                    <Select
                      size="sm"
                      w="80px"
                      value={txnRowsPerPage}
                      onChange={(e) => {
                        setTxnRowsPerPage(Number(e.target.value));
                        setTxnPage(1);
                      }}
                    >
                      {[5, 10, 25, 50, 100].map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </Select>
                  </HStack>
                  <HStack spacing={3}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTxnPage(p => Math.max(p - 1, 1))}
                      isDisabled={txnPage === 1}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {txnPage} of {Math.ceil(selectedTransactionLogs.length / txnRowsPerPage)}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setTxnPage(p => Math.min(p + 1, Math.ceil(selectedTransactionLogs.length / txnRowsPerPage)))
                      }
                      isDisabled={txnPage >= Math.ceil(selectedTransactionLogs.length / txnRowsPerPage)}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => setIsTxnOpen(false)}
              colorScheme="teal"
              variant="solid"
              borderRadius="lg"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ✅ Confirmation Alert Modal */}
<Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
  <ModalOverlay />
  <ModalContent>
    <ModalHeader>Confirm Action</ModalHeader>
    <ModalCloseButton />
    <ModalBody>
      <Text>
        Are you sure you want to save this changes?{" "}
      </Text>
    </ModalBody>
    <ModalFooter>
      <Button variant="ghost" mr={3} onClick={onConfirmClose}>
        Cancel
      </Button>
      <Button
        colorScheme="green"
        onClick={() => {
          onConfirmClose();
          executeAction("update", [editFormData.id], { ...editFormData, forceApprove: true });
        }}
      >
        Yes, Save Changes
      </Button>
    </ModalFooter>
  </ModalContent>
</Modal>

    </>
  );
}